import type { Player } from '../../types/player';
import { VAL_CFG } from './config';

export function isValidFantasyPlayer(player: any, includeDefenses: boolean = true): boolean {
  const defensivePositions = ['CB', 'S', 'FS', 'SS', 'LB', 'ILB', 'OLB', 'MLB', 'DE', 'DT', 'NT', 'DL', 'EDGE'];
  if (defensivePositions.includes(player.position)) return false;
  const offensiveLinePositions = ['OL', 'C', 'G', 'LG', 'RG', 'T', 'LT', 'RT', 'OT'];
  if (offensiveLinePositions.includes(player.position)) return false;
  const teamDefensePositions = ['DEF', 'DST', 'D/ST'];
  if (teamDefensePositions.includes(player.position)) return includeDefenses;
  const validFantasyPositions = ['QB', 'RB', 'WR', 'TE', 'K'];
  if (!validFantasyPositions.includes(player.position)) return false;
  if (player.isActive === false || player.active === false) return false;
  // Allow free agents or players without a current team assignment; they may sign before the season
  return true;
}

export function filterPlayersWithSummary(players: any[], context: string = 'Unknown', includeDefenses: boolean = true): any[] {
  const totalPlayers = players.length;
  const validPlayers = players.filter(player => isValidFantasyPlayer(player, includeDefenses));
  const filteredCount = totalPlayers - validPlayers.length;
  const defenseNote = includeDefenses ? ' (including team defenses)' : ' (excluding team defenses)';
  console.log(`${context}: Filtered ${filteredCount} non-fantasy players from ${totalPlayers} total players. ${validPlayers.length} fantasy players remaining${defenseNote}.`);
  return validPlayers;
}

export function meetsMinimumProjectionThreshold(player: Player): boolean {
  const threshold = VAL_CFG.thresholds[player.position] ?? 100;
  return (player.projection || 0) >= threshold;
}

export function scalePlayerValuesToTen<T extends { value: number | undefined | null }>(players: T[]): T[] {
  // Keep the original complex scoring system for ranking purposes
  // This function should preserve the actual calculated scores
  const values = players.map(p => (isFinite(p.value as number) ? (p.value as number) : 0));
  if (values.length === 0) return players.map(p => ({ ...p, value: 0 })) as T[];
  
  // Don't modify the values - keep the original scoring system
  return players;
}

export function applyTierBasedDisplayValues<T extends { value: number | undefined | null }>(players: T[]): T[] {
  // Apply tier-based display values AFTER ranking is complete
  return players.map((p, index) => {
    let tierValue: number;
    
    if (index < 12) {
      // Very High - Top 12 players (first round picks)
      tierValue = 5.0;
    } else if (index < 24) {
      // High - Next 12 players (second round picks) 
      tierValue = 4.0;
    } else if (index < 48) {
      // Medium - Next 24 players (rounds 3-4)
      tierValue = 3.0;
    } else if (index < 84) {
      // Low - Next 36 players (rounds 5-7)
      tierValue = 2.0;
    } else {
      // Very Low - Likely undraftable/bench/waiver wire
      tierValue = 1.0;
    }
    
    return { ...p, value: tierValue } as T;
  });
}

export function enforcePositionDistribution(players: Player[]): Player[] {
  const targets = VAL_CFG.distribution.targets;
  const maxLimits = VAL_CFG.distribution.caps;
  const filtered = players.filter(meetsMinimumProjectionThreshold);
  const groups: Record<string, Player[]> = { QB: [], RB: [], WR: [], TE: [] } as any;
  for (const p of filtered) {
    if (!groups[p.position]) groups[p.position] = [];
    groups[p.position].push(p);
  }
  Object.keys(groups).forEach(pos => {
    groups[pos] = groups[pos].sort((a, b) => (b.value || 0) - (a.value || 0));
  });
  const assembled: Player[] = [];
  const addFromGroup = (pos: string, count: number) => {
    const take = Math.min(count, (groups[pos] || []).length);
    for (let i = 0; i < take; i++) assembled.push(groups[pos][i]);
    groups[pos] = (groups[pos] || []).slice(take);
  };
  Object.entries(targets).forEach(([pos, count]) => addFromGroup(pos, count));
  const remaining: Player[] = ([] as Player[]).concat(...Object.values(groups));
  remaining.sort((a, b) => (b.value || 0) - (a.value || 0));
  const counts: Record<string, number> = { QB: 0, RB: 0, WR: 0, TE: 0 } as any;
  assembled.forEach(p => { counts[p.position] = (counts[p.position] || 0) + 1; });
  for (const p of remaining) {
    if (assembled.length >= 100) break;
    const pos = p.position;
    const current = counts[pos] || 0;
    const max = maxLimits[pos] ?? 100;
    if (current < max) {
      assembled.push(p);
      counts[pos] = current + 1;
    }
  }
  return assembled.sort((a, b) => (b.value || 0) - (a.value || 0));
}


